<template lang="pug">
.vue-search-box(:class="{'vue-search-box--nofirst': !historyMode, 'vue-search-box--sp': isSmartPhone, 'vue-search-box--news': searchCategory === 'news'}")
  .vue-search-box-inner.u-flex(:class="{'vue-search-box-inner--focus': hasFocus}")
    .vue-search-box-category(
      v-if="!isSmartPhone"
      :style="{width:categoryWidth+'px'}"
    )
      select(
        v-model="category"
        name="category"
        @change="onChange"
        :style="{width:selectWidth+'px'}"
      )
        option(v-for="cat in categories", :value="cat") {{ cat.label }}

    .vue-search-box-field
      v-select(
        v-model="keyword"
        ref="vselect"
        taggable
        @input="onSubmit(true)"
        @search="getOptions"
        :options="options"
        :filter="nullFilter"
        :selectable="selectable"
        :map-keydown="mapKeydown"
        :placeholder="placeholder"
        :clear-search-on-blur="function(){return false}"
        v-on:search:focus="onFocus"
        v-on:search:blur="onBlur"
      )
        template(#option="option")
          .vs__box
            span.vue-search-box-option.vs__box_item
              span(v-html="formatOptionHtml(option)")
            span.vue-search-box-option.vs__box_item.vs__box_item_last(v-if="historyMode && option.head")
              button.vs__delete_all(@mousedown.stop="deleteAllHistory()")
                | Clear all
            span.vue-search-box-option.vs__box_item.vs__box_item_last(v-else-if="historyMode")
              button.vs__delete_tag(@mousedown.stop="deleteHistory(option)")

    .vue-search-box-submit
      button(@click.stop="onSubmit(false)")
        i.icon-search
</template>

<script>
import Vue from 'vue';
import VueSelect from 'vue-select';
import axios from '../../api/common/axios';
import searchApi from '../../api/search';
import escapeHtml from 'latest_modules/lodash/escape';
import { SHOP_SEARCH_CATEGORIES } from '../../../../../../lib/document/schema/search_categories';
import { removeWordFromSearchHistories } from '../../../../../../lib/util/search_histories';

Vue.component('v-select', VueSelect);

const escapeStrong = (html) => `<strong>${escapeHtml(html)}</strong>`;

const fuzzscan = (text, needle) => {
  if (!text || !needle) {
    return {};
  }

  let start = 0;
  const n = text.length;
  const head = needle.charAt(0).toLowerCase();
  for (; ; start++) {
    if (start >= n) {
      return {};
    }
    let c = text.charAt(start).toLowerCase();
    if (c === head) {
      break;
    }
  }

  let end = start + 1;
  let j = 1;
  const m = needle.length;
  while (end < n && j < m) {
    let c = text.charAt(end++).toLowerCase();
    let d = needle.charAt(j++).toLowerCase();
    if (c !== d) {
      break;
    }
  }

  return {
    start,
    end,
    length: end - start
  };
};

let debounceTimeout;
const debounce = (func, wait) => {
  return function (...args) {
    const context = this;
    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => {
      func.apply(context, args);
    }, wait);
  };
};

// コメントアウトしてる値を使用すると WebKit で丁度良い幅になるが
// Gecko で見切れる。
// WebKit 用の値は CSS の値そのままなので Gecko の挙動がよく分からない。
//const selectPad = 28;
const selectPad = 36;
const categoryPad = 1;

export default {
  components: {
    'v-select': VueSelect
  },

  props: {
    gaTagSettings: {
      type: Object,
      default() {
        return {};
      }
    },
    searchCategory: {
      type: String,
      default: 'shop'
    }
  },

  data() {
    const isNews = this.searchCategory === 'news';

    let categories;
    if (isNews) {
      categories = [
        {
          label: 'News',
          value: 'news'
        },
        {
          label: 'Shop',
          value: ''
        }
      ];
    } else {
      categories = [
        {
          label: 'Shop',
          value: ''
        },
        ...SHOP_SEARCH_CATEGORIES
      ];
    }

    return {
      category: categories[0],
      keyword: '',
      maxlength: 64,
      options: [],
      histories: [],
      historyMode: false,
      reopenOnBlur: false,
      optionHtmlCache: {},
      inputId: 'js-searchbox-input',
      categories,
      placeholder: isNews ? 'Anime, Naruto, Miku etc...' : 'Search Products...',
      selectWidth: 34 + selectPad,
      hasFocus: false
    };
  },

  mounted() {
    let uri = window.location.search.substring(1);
    let params = new URLSearchParams(uri);

    const isNews = this.searchCategory === 'news';
    const keyword = params.get(isNews ? 'q' : 'keyword');
    if (keyword !== null) {
      this.$refs.vselect.search = keyword;
      this.$nextTick(() => {
        // vue-select内のsearchのwatchでopenがtrueになるためfalseにする
        this.$refs.vselect.open = false;
      });
    }

    if (isNews) {
      this.onChange();
    } else {
      const cat = params.get('category');
      if (cat !== null) {
        const category = this.categories.find((c) => c.value === cat);
        if (category) {
          this.category = category;
        }
      }

      searchApi.histories().then((data) => {
        this.histories = data.histories;
        this.onChange();
      });
    }
  },

  computed: {
    isSmartPhone() {
      return window.tom.user.isSmartPhoneDevice;
    },

    categoryWidth() {
      return this.selectWidth + categoryPad;
    }
  },

  methods: {
    getOptions(search, loading) {
      if (search.length > this.maxlength) {
        this.keyword = search.substr(0, this.maxlength);
        this.$refs.vselect.search = this.keyword;
        return false;
      }
      this.search(loading, search, this);
    },

    nullFilter(optionList, search) {
      return search ? optionList : [];
    },

    selectable(option) {
      if (this.historyMode && option.head) {
        return false;
      } else {
        return true;
      }
    },

    setOptionsFromHistory() {
      this.optionHtmlCache = {};
      if (this.histories.length) {
        this.options = [
          {
            label: 'Recent',
            head: true
          },
          ...this.histories
        ];
      } else {
        this.options = this.histories;
      }
    },

    deleteAllHistory() {
      axios.post('/search/api/delete_all_histories');

      this.histories = [];
      if (this.historyMode) {
        this.setOptionsFromHistory();
      }
    },

    deleteHistory(option) {
      const word = option.label;
      axios.post('/search/api/delete_history', {
        word
      });

      this.histories = removeWordFromSearchHistories(
        this.histories,
        option.label
      );
      if (this.historyMode) {
        this.setOptionsFromHistory();
        if (this.histories.length) {
          this.reopenOnBlur = true;
        }
      }
    },

    formatOptionHtml(option) {
      let text = this.$refs.vselect.search;
      const cacheKey = `${text}:${option.label}`;
      if (this.optionHtmlCache[cacheKey]) {
        return this.optionHtmlCache[cacheKey];
      }

      let html = null;
      if (text) {
        const { start, end, length } = fuzzscan(option.label, text);
        if (length) {
          let head = '';
          const body = escapeHtml(option.label.slice(start, end));
          let tail = '';
          if (start > 0) {
            head = escapeStrong(option.label.slice(0, start));
          }
          if (end < option.label.length) {
            tail = escapeStrong(option.label.slice(end));
          }
          html = `${head}${body}${tail}`;
        }
      }

      if (!html) {
        html = escapeStrong(option.label);
      }
      this.optionHtmlCache[cacheKey] = html;
      return html;
    },

    onFocus() {
      this.hasFocus = true;
    },

    onBlur() {
      this.hasFocus = false;
      if (this.reopenOnBlur) {
        this.reopenOnBlur = false;
        this.$refs.vselect.toggleDropdown({
          target: this.$refs.vselect.searchEl
        });
      }
    },

    onChange() {
      this.search(false, this.$refs.vselect.search || '', this);

      const div = document.createElement('div');
      div.style.position = 'absolute';
      div.style.visibility = 'hidden';
      div.style.display = 'block';
      const span = document.createElement('span');
      span.innerText = this.category.label;
      div.appendChild(span);
      this.$el.appendChild(div);
      const width = Math.ceil(span.getBoundingClientRect().width);
      this.selectWidth = width + selectPad;
      this.$el.removeChild(div);
    },

    search(loading, search, vm) {
      if (this.category.value === 'news') {
        vm.historyMode = false;
        vm.options = [];
        return;
      }
      if (search === '') {
        vm.historyMode = true;
        vm.setOptionsFromHistory();
        this.$nextTick(() => {
          this.$refs.vselect.typeAheadPointer = -1;
        });
        return;
      }

      vm.historyMode = false;
      debounce(() => {
        axios
          .get(
            `/search/complete_product?category=${
              this.searchCategory === 'news' ? '' : this.category.value
            }&keyword=${encodeURIComponent(search)}`
          )
          .then((response) => {
            vm.optionHtmlCache = {};
            if (search === this.$refs.vselect.search) {
              vm.options = response.data;
            }
          });
      }, 500)();
    },

    mapKeydown(map) {
      map[9] = (e) => {
        if (this.historyMode) {
          if (this.options && this.options[1]) {
            this.$refs.vselect.search = this.options[1];
            e.preventDefault();
          }
        } else {
          if (this.options && this.options[0]) {
            this.$refs.vselect.search = this.options[0];
            e.preventDefault();
          }
        }
        return true;
      };
      if (this.$refs.vselect.typeAheadPointer === -1) {
        map[13] = (e) => {
          this.onSubmit();
          e.preventDefault();
        };
      }
      return map;
    },

    onSubmit(triggerFromSelect) {
      let keyword;
      if (triggerFromSelect) {
        if (this.keyword === null) {
          return;
        }
        keyword = this.keyword;
        if (typeof keyword === 'object') {
          if (keyword.label) {
            keyword = keyword.label;
          } else {
            return;
          }
        }
      } else {
        // 検索ボタンが押された場合は this.keyword と input の値がずれている
        // ことがあり常に input の値を使う
        keyword = this.$refs.vselect.search;
      }
      keyword = encodeURIComponent(keyword || '');

      // GA
      const action = triggerFromSelect ? 'Suggested' : 'Searched';
      let searchCategory = window.location.pathname === '/news' ? 'News' : 'EC';
      if (Object.keys(this.gaTagSettings).length) {
        searchCategory = this.gaTagSettings.category;
      }
      tom.helper.ga.push(['event', searchCategory, action, keyword, 1]);

      if (this.searchCategory === 'news' && this.category.value === 'news') {
        window.location = '/news?q=' + keyword;
      } else {
        window.location =
          '/search?category=' + this.category.value + '&keyword=' + keyword;
      }
    }
  }
};
</script>

<style>
.vue-search-box .v-select {
  position: relative;
  font-family: inherit;
}

.vue-search-box .v-select,
.vue-search-box .v-select * {
  box-sizing: border-box;
}

@-webkit-keyframes vSelectSpinner {
  0% {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(1turn);
  }
}

@keyframes vSelectSpinner {
  0% {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(1turn);
  }
}

.vue-search-box .vs__fade-enter-active,
.vue-search-box .vs__fade-leave-active {
  pointer-events: none;
  transition: opacity 0.15s cubic-bezier(1, 0.5, 0.8, 1);
}

.vue-search-box .vs__fade-enter,
.vue-search-box .vs__fade-leave-to {
  opacity: 0;
}

.vue-search-box .vs--disabled .vs__clear,
.vue-search-box .vs--disabled .vs__dropdown-toggle,
.vue-search-box .vs--disabled .vs__open-indicator,
.vue-search-box .vs--disabled .vs__search,
.vue-search-box .vs--disabled .vs__selected {
  cursor: not-allowed;
  background-color: #f8f8f8;
}

.vue-search-box .v-select[dir='rtl'] .vs__actions {
  padding: 0 3px 0 6px;
}

.vue-search-box .v-select[dir='rtl'] .vs__clear {
  margin-left: 6px;
  margin-right: 0;
}

.vue-search-box .v-select[dir='rtl'] .vs__deselect {
  margin-left: 0;
  margin-right: 2px;
}

.vue-search-box .v-select[dir='rtl'] .vs__dropdown-menu {
  text-align: right;
}

.vue-search-box .vs__dropdown-toggle {
  height: 42px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  display: flex;
  padding: 0 0 4px;
  background: none;
  white-space: normal;
  border: none;
  border-radius: 0;
}

.vue-search-box select {
  height: 42px;
}

.vue-search-box .vs__selected-options {
  display: flex;
  flex-basis: 100%;
  flex-grow: 1;
  flex-wrap: wrap;
  padding: 0 2px;
  position: relative;
}

.vue-search-box .vs__actions {
  display: none;
}

.vue-search-box .vs--searchable .vs__dropdown-toggle {
  cursor: text;
}

.vue-search-box .vs--unsearchable .vs__dropdown-toggle {
  cursor: pointer;
}

.vue-search-box .vs--open .vs__dropdown-toggle {
  border-bottom-color: transparent;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.vue-search-box .vs__open-indicator {
  fill: rgba(60, 60, 60, 0.5);
  transform: scale(1);
  transition: transform 0.15s cubic-bezier(1, -0.115, 0.975, 0.855);
  transition-timing-function: cubic-bezier(1, -0.115, 0.975, 0.855);
}

.vue-search-box .vs--open .vs__open-indicator {
  transform: rotate(180deg) scale(1);
}

.vue-search-box .vs--loading .vs__open-indicator {
  opacity: 0;
}

.vue-search-box .vs__clear {
  fill: rgba(60, 60, 60, 0.5);
  padding: 0;
  border: 0;
  background-color: transparent;
  cursor: pointer;
  margin-right: 8px;
}

.vue-search-box .vs__dropdown-menu {
  display: block;
  box-sizing: border-box;
  position: absolute;
  top: calc(100% - 1px);
  left: 0;
  z-index: 1000;
  padding: 0;
  margin: 0;
  width: 100%;
  max-height: 350px;
  min-width: 160px;
  overflow-y: auto;
  box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(60, 60, 60, 0.26);
  border-top-style: none;
  border-radius: 0 0 4px 4px;
  text-align: left;
  list-style: none;
  background: #fff;
}

.vue-search-box .vs__dropdown-menu .vs__box {
  display: flex;
}

.vs__box_item_last {
  margin-left: auto;
}

.vs__delete_all {
  vertical-align: middle;
  line-height: 1.714285716;
  color: #56beff !important;
}

.vs__delete_tag::before {
  content: ' \e007';
  font-family: 'tomicon';
  font-style: normal;
  font-weight: normal;
  vertical-align: middle;
  line-height: 1.714285716;
  color: #56beff;
}

.vue-search-box--nofirst .vs__dropdown-menu li:first-child {
  display: none;
}

.vue-search-box .vs__no-options {
  display: none;
  text-align: center;
}

.vue-search-box .vs__dropdown-menu:has(> .vs__no-options) {
  display: none;
}

.vue-search-box .vs__dropdown-option {
  display: block;
  padding: 3px 20px;
  clear: both;
  color: #333;
  white-space: nowrap;
}

.vue-search-box-option {
  line-height: 1.714285716;
  font-size: 16px;
}

.vue-search-box .vs__dropdown-option:hover {
  cursor: pointer;
}

.vue-search-box .vs__dropdown-option--highlight {
  background: #5897fb;
  color: #fff;
}

.vue-search-box .vs__dropdown-option--disabled {
  background: inherit;
  color: rgba(60, 60, 60, 0.5);
}

.vue-search-box .vs__dropdown-option--disabled:hover {
  cursor: inherit;
}

.vue-search-box .vs__selected {
  display: flex;
  align-items: center;
  background-color: #f0f0f0;
  border: 1px solid rgba(60, 60, 60, 0.26);
  border-radius: 4px;
  color: #333;
  line-height: 1.4;
  margin: 4px 2px 0;
  padding: 0 0.25em;
  z-index: 0;
}

.vue-search-box .vs__deselect {
  display: inline-flex;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  margin-left: 4px;
  padding: 0;
  border: 0;
  cursor: pointer;
  background: none;
  fill: rgba(60, 60, 60, 0.5);
  text-shadow: 0 1px 0 #fff;
}

.vue-search-box .vs--single .vs__selected {
  background-color: transparent;
  border-color: transparent;
}

.vue-search-box .vs--single.vs--open .vs__selected {
  height: 42px;
  margin-top: 0;
  position: absolute;
  opacity: 0.4;
}

.vue-search-box .vs--single.vs--searching .vs__selected {
  display: none;
}

.vue-search-box .vs__search::-webkit-search-cancel-button {
  display: none;
}

.vue-search-box .vs__search::-ms-clear,
.vue-search-box .vs__search::-webkit-search-decoration,
.vue-search-box .vs__search::-webkit-search-results-button,
.vue-search-box .vs__search::-webkit-search-results-decoration {
  display: none;
}

.vue-search-box .vs__selected {
  font-size: 18px;
}

.vue-search-box .vs__search,
.vue-search-box .vs__search:focus {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  line-height: 1.4;
  font-size: 18px;
  border: 1px solid transparent;
  border-left: none;
  outline: none;
  margin: 4px 0 0;
  padding: 0 7px;
  background: none;
  box-shadow: none;
  width: 0;
  max-width: 100%;
  flex-grow: 1;
  z-index: 1;
}

.vue-search-box .vs__search::-webkit-input-placeholder,
.vue-search-box .vs__search::-moz-placeholder,
.vue-search-box .vs__search:-ms-input-placeholder,
.vue-search-box .vs__search::-ms-input-placeholder,
.vue-search-box .vs__search::placeholder {
  color: #747474;
}

.vue-search-box .vs--unsearchable .vs__search {
  opacity: 1;
}

.vue-search-box .vs--unsearchable:not(.vs--disabled) .vs__search:hover {
  cursor: pointer;
}

.vue-search-box
  .vs--single.vs--searching:not(.vs--open):not(.vs--loading)
  .vs__search {
  opacity: 0.78;
}

.vue-search-box .vs__spinner {
  align-self: center;
  opacity: 0;
  font-size: 5px;
  text-indent: -9999em;
  overflow: hidden;
  border: 0.9em solid hsla(0, 0%, 39.2%, 0.1);
  border-left-color: rgba(60, 60, 60, 0.45);
  transform: translateZ(0);
  -webkit-animation: vSelectSpinner 1.1s linear infinite;
  animation: vSelectSpinner 1.1s linear infinite;
  transition: opacity 0.1s;
}

.vue-search-box .vs__spinner,
.vue-search-box .vs__spinner:after {
  border-radius: 50%;
  width: 5em;
  height: 5em;
}

.vue-search-box .vs--loading .vs__spinner {
  opacity: 1;
}
</style>

<style scoped>
.vue-search-box-inner {
  border: 2px solid #d0d0d0;
  background-color: #d0d0d0;
  border-radius: 2px;
  transition:
    background-color 0.25s linear,
    border-color 0.25s linear;
}

.vue-search-box-category {
  overflow: hidden;
  max-width: 40%;
  min-width: 80px;
  text-align: center;
  position: relative;
  border-right: 1px solid #d0d0d0;
  background: #f3f3f3;
  border-top-left-radius: 2px;
  border-bottom-left-radius: 2px;
  flex-shrink: 0;
}

.vue-search-box-category select {
  max-width: 100%;
  padding-right: 1em;
  cursor: pointer;
  text-indent: 0.01px;
  text-overflow: ellipsis;
  border: none;
  outline: none;
  background: transparent;
  background-image: none;
  box-shadow: none;
  appearance: none;
  padding: 11px 20px 11px 8px;
  color: #000;
}

.vue-search-box-category select::-ms-expand {
  display: none;
}

.vue-search-box-category::before {
  position: absolute;
  top: 1.2em;
  right: 0.5em;
  width: 0;
  height: 0;
  padding: 0;
  content: '';
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 6px solid #000;
  pointer-events: none;
}

.vue-search-box-field {
  width: 100%;
  height: 42px;
  position: relative;
  background-color: #fff;
}

.vue-search-box-field input {
  height: 42px;
  font-size: 18px;
}

.vue-search-box-field a,
.vue-search-box-field button {
  display: block;
  color: #000;
  background: none;
  border: none;
}

.vue-search-box-field button {
  width: 100%;
  text-align: left;
}

.vue-search-box-submit a,
.vue-search-box-submit button {
  width: 45px;
  height: 42px;
  color: #fff;
  font-size: 15px;
  text-align: center;
  background-color: #f08300;
  border: none;
  border-top-right-radius: 2px;
  border-bottom-right-radius: 2px;
  line-height: 42px;
  cursor: pointer;
  transition: all 0.25s linear;
  display: block;
  z-index: 9999;
}

/* SP */
.vue-search-box--sp {
  background-color: #3ea2d9;
  padding: 10px;
}

.vue-search-box--sp.vue-search-box--news {
  background-color: #ee3c72;
}

.vue-search-box--sp .vue-search-box-field {
  border-top-left-radius: 2px;
  border-bottom-left-radius: 2px;
}

.vue-search-box--sp .vue-search-box-option {
  line-height: 2.142857145;
}

.vue-search-box-inner.vue-search-box-inner--focus {
  background-color: #ff9500;
  border-color: #ff9500;
}
</style>
